/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  PLAYERNAMEMODAL.TSX - Set Vanity Name Component                          ║
 * ║  Lets players set a custom display name (paid in test FRAME on the demo)    ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ethers } from 'ethers';
import { useVanityNames, AVATAR_OPTIONS, VANITY_NAME_COST_USD } from '../hooks/useVanityNames';
import { getFRAMEContract, getGameAddress } from '../utils/contract';
import { fetchPAWPrice } from '../utils/price';
import toast from 'react-hot-toast';
import { shortWallet } from '../utils/drones';

interface PlayerNameModalProps {
  isOpen: boolean;
  onClose: () => void;
  address: string;
}

export function PlayerNameModal({ isOpen, onClose, address }: PlayerNameModalProps) {
  const { myProfile, setProfile, getAvatar } = useVanityNames();
  const [name, setName] = useState(myProfile?.name || '');
  const [selectedAvatar, setSelectedAvatar] = useState(myProfile?.avatar || getAvatar(address));
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [framePrice, setFramePrice] = useState(0);
  const [frameBalance, setFrameBalance] = useState(0n);
  const [step, setStep] = useState<'customize' | 'confirm' | 'paying'>('customize');

  // Already paid = free edits
  const alreadyPaid = myProfile?.paid === true;

  // FRAME cost derived from the live FRAME price. Pre-launch (testnet demo) there is no price,
  // so this stays 0n and the UI must not show any dollar figure or invented amount.
  const pawCost = useMemo(() => {
    if (framePrice <= 0) return 0n;
    const pawAmount = VANITY_NAME_COST_USD / framePrice;
    // Round up to whole FRAME tokens
    return ethers.parseEther(Math.ceil(pawAmount).toString());
  }, [framePrice]);

  const pawCostFormatted = useMemo(() => {
    if (pawCost === 0n) return '...';
    const num = Number(ethers.formatEther(pawCost));
    if (num >= 1e15) return `${(num / 1e15).toFixed(1)}Q`;
    if (num >= 1e12) return `${(num / 1e12).toFixed(1)}T`;
    if (num >= 1e9)  return `${(num / 1e9).toFixed(1)}B`;
    if (num >= 1e6)  return `${(num / 1e6).toFixed(1)}M`;
    if (num >= 1e3)  return `${(num / 1e3).toFixed(1)}K`;
    return num.toFixed(0);
  }, [pawCost]);

  // Fetch FRAME price and balance
  useEffect(() => {
    if (!isOpen) return;
    const fetchData = async () => {
      try {
        const price = await fetchPAWPrice();
        setFramePrice(price);
        
        if (address) {
          const frameContract = await getFRAMEContract();
          if (frameContract) {
            const bal = await frameContract.balanceOf(address);
            setFrameBalance(bal);
          }
        }
      } catch (e) {
        console.error('Failed to fetch FRAME data:', e);
      }
    };
    fetchData();
  }, [isOpen, address]);

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setName(myProfile?.name || '');
      setSelectedAvatar(myProfile?.avatar || getAvatar(address));
      setError(null);
      setSuccess(false);
      setStep(alreadyPaid ? 'customize' : 'customize');
    }
  }, [isOpen, myProfile, getAvatar, address, alreadyPaid]);

  const hasEnoughPAW = frameBalance >= pawCost;

  const handleNext = useCallback(async () => {
    const cleanName = name.trim();
    if (cleanName.length < 2) {
      setError('Name must be at least 2 characters');
      return;
    }
    if (cleanName.length > 20) {
      setError('Name must be 20 characters or less');
      return;
    }
    setError(null);
    
    if (alreadyPaid) {
      // Free edit — save directly (name persists across all rounds)
      try {
        await setProfile(address, cleanName, selectedAvatar);
        setSuccess(true);
        setTimeout(() => {
          onClose();
          setSuccess(false);
        }, 1000);
      } catch (err: any) {
        setError(err.message || 'Failed to save');
      }
    } else {
      // Need payment — show confirmation
      setStep('confirm');
    }
  }, [name, selectedAvatar, address, alreadyPaid, setProfile, onClose]);

  const handlePay = useCallback(async () => {
    if (isPaying) return;
    setIsPaying(true);
    setStep('paying');
    setError(null);

    try {
      const frameContract = await getFRAMEContract(true);
      if (!frameContract) throw new Error('Could not connect to FRAME contract');
      
      const gameAddress = getGameAddress();
      if (!gameAddress) throw new Error('Game contract address not configured');

      toast.loading('Sending test FRAME payment...', { id: 'vanity-pay' });
      
      // Transfer FRAME to the game contract as revenue
      const tx = await frameContract.transfer(gameAddress, pawCost);
      toast.loading('Confirming transaction...', { id: 'vanity-pay' });
      const receipt = await tx.wait();
      
      // Payment confirmed — save the name (persists across all rounds)
      const cleanName = name.trim();
      await setProfile(address, cleanName, selectedAvatar, receipt.hash);
      
      toast.success('🎉 Vanity name set — yours forever!', { id: 'vanity-pay' });
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setStep('customize');
      }, 1500);
    } catch (err: any) {
      console.error('Vanity name payment failed:', err);
      const msg = err?.reason || err?.message || 'Payment failed';
      setError(msg);
      toast.error(msg, { id: 'vanity-pay' });
      setStep('confirm');
    } finally {
      setIsPaying(false);
    }
  }, [isPaying, pawCost, name, selectedAvatar, address, setProfile, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-gray-900/95 border border-neon-cyan/30 rounded-2xl max-w-md w-full p-6 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="text-center mb-6">
              <motion.div 
                className="text-6xl mb-3"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 0.5 }}
              >
                {selectedAvatar}
              </motion.div>
              <h2 className="text-xl font-orbitron text-neon-cyan">
                {alreadyPaid ? 'Edit Your Name' : '✨ Get a Vanity Name'}
              </h2>
              <p className="text-gray-500 font-rajdhani text-sm">
                {alreadyPaid 
                  ? 'Update your display name for free — works in every round'
                  : 'One-time purchase — your name persists across ALL rounds!'
                }
              </p>
              {!alreadyPaid && pawCost > 0n && (
                <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-nixie-orange/20 border border-nixie-orange/50">
                  <span className="text-nixie-orange font-orbitron text-sm font-bold">
                    {pawCostFormatted} test FRAME
                  </span>
                </div>
              )}
            </div>

            {step === 'customize' && (
              <>
                {/* Name input */}
                <div className="mb-4">
                  <label className="block text-gray-400 text-sm font-rajdhani mb-1">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Enter your name..."
                    maxLength={20}
                    className="w-full bg-black/50 border border-neon-cyan/30 rounded-lg px-4 py-3 text-white font-rajdhani focus:outline-none focus:border-neon-cyan text-lg"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>{name.length}/20 characters</span>
                    <span>Min 2 characters</span>
                  </div>
                </div>

                {/* Avatar selection */}
                <div className="mb-6">
                  <label className="block text-gray-400 text-sm font-rajdhani mb-2">
                    Choose Avatar
                  </label>
                  <div className="grid grid-cols-8 gap-2">
                    {AVATAR_OPTIONS.map((avatar) => (
                      <button
                        key={avatar}
                        onClick={() => setSelectedAvatar(avatar)}
                        className={`text-2xl p-2 rounded-lg transition-all ${
                          selectedAvatar === avatar
                            ? 'bg-neon-cyan/20 ring-2 ring-neon-cyan scale-110'
                            : 'bg-gray-800/50 hover:bg-gray-700/50'
                        }`}
                      >
                        {avatar}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Preview */}
                <div className="mb-6 p-4 bg-gray-800/50 rounded-lg">
                  <div className="text-xs text-gray-500 mb-2">Preview</div>
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{selectedAvatar}</span>
                    <div>
                      <div className="text-white font-rajdhani text-lg">
                        {name || 'Your Name'}
                      </div>
                      <div className="text-gray-500 font-mono text-xs">
                        {shortWallet(address)}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {step === 'confirm' && (
              <div className="mb-6">
                {/* Payment confirmation */}
                <div className="p-4 bg-void-black/60 rounded-xl border border-nixie-orange/30 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-gray-400 font-rajdhani text-sm">Your Name</span>
                    <span className="text-white font-rajdhani text-lg">{selectedAvatar} {name}</span>
                  </div>
                  <div className="border-t border-gray-700/50 pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-400 font-rajdhani text-sm">Cost</span>
                      <span className="text-nixie-orange font-orbitron">{pawCost > 0n ? `${pawCostFormatted} test FRAME` : '0 test FRAME (gas only)'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 font-rajdhani text-sm">Your Balance</span>
                      <span className={`font-mono text-sm ${hasEnoughPAW ? 'text-green-400' : 'text-red-400'}`}>
                        {Number(ethers.formatEther(frameBalance)).toLocaleString(undefined, { maximumFractionDigits: 0 })} test FRAME
                      </span>
                    </div>
                  </div>
                </div>

                {!hasEnoughPAW && (
                  <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm font-rajdhani mb-4">
                    ⚠️ Not enough test FRAME. You need {pawCostFormatted} test FRAME.
                  </div>
                )}

                <p className="text-gray-500 text-xs font-rajdhani text-center mb-4">
                  💎 One-time purchase — your name carries across ALL rounds and games forever.
                  Free edits anytime after purchase.
                </p>
              </div>
            )}

            {step === 'paying' && (
              <div className="mb-6 text-center py-8">
                <motion.div 
                  className="text-5xl mb-4"
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  ⏳
                </motion.div>
                <p className="text-neon-cyan font-orbitron text-sm mb-2">Processing Payment</p>
                <p className="text-gray-500 font-rajdhani text-sm">Confirming transaction on Base...</p>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm font-rajdhani">
                ⚠️ {error}
              </div>
            )}

            {/* Success message */}
            {success && (
              <div className="mb-4 p-3 bg-green-500/20 border border-green-500/50 rounded-lg text-green-400 text-sm font-rajdhani text-center">
                ✅ Vanity name set! Works across all rounds — forever yours.
              </div>
            )}

            {/* Buttons */}
            {step === 'customize' && (
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-xl font-orbitron transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleNext}
                  disabled={name.trim().length < 2}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-neon-cyan to-neon-magenta text-black font-orbitron rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {alreadyPaid ? 'Save' : 'Continue'}
                </button>
              </div>
            )}

            {step === 'confirm' && (
              <div className="flex gap-3">
                <button
                  onClick={() => setStep('customize')}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-xl font-orbitron transition-colors text-sm"
                >
                  Back
                </button>
                <button
                  onClick={handlePay}
                  disabled={!hasEnoughPAW || isPaying}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-nixie-orange to-neon-magenta text-black font-orbitron rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {pawCost > 0n ? `💎 Pay ${pawCostFormatted} test FRAME` : 'Confirm in wallet'}
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Inline player name display component
interface PlayerNameDisplayProps {
  address: string;
  showAvatar?: boolean;
  showAddress?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function PlayerNameDisplay({
  address,
  showAvatar = true,
  showAddress = false,
  className = '',
  size = 'md',
}: PlayerNameDisplayProps) {
  const { getDisplayName, getAvatar } = useVanityNames();

  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  const avatarSizes = {
    sm: 'text-base',
    md: 'text-xl',
    lg: 'text-2xl',
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {showAvatar && (
        <span className={avatarSizes[size]}>{getAvatar(address)}</span>
      )}
      <div>
        <div className={`font-rajdhani text-white ${sizeClasses[size]}`}>
          {getDisplayName(address)}
        </div>
        {showAddress && (
          <div className="text-gray-500 font-mono text-xs">
            {shortWallet(address)}
          </div>
        )}
      </div>
    </div>
  );
}
